import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setIsLoader } from "@features/loaderSlice";
import { mainContext } from "@ts/constants";
import DatePicker from "@drop-downs/DatePicker";
import {
    AppDispatch,
    IAccess,
    IChannel,
    IChannels,
    IClientAccess,
    IMainContext,
    ITitle,
    IValueBoolean,
    ParentModule,
    Platforms,
    Roles,
    SubModule,
    TChannelSelectOptions,
    TReportsData,
    TUserdata,
    AdminSettingsTabs,
    ICampaignCreationDisplay,
} from "@ts/common-types";
import $ from "jquery";
import { setPortfoliosData } from "@features/gridDataSlice";
import { Button, Select } from "antd";
import {
    filterSelectOptionsWithIdAndName,
    getActiveModule,
    getClientId,
    resetMediaPlanningConfig,
    titleCase,
    clearClientTimezone,
    setActivationChannel,
    setActivationConfig,
} from "@ts/utils";
import { FaPaperPlane, FaTimes } from "react-icons/fa";
import { usePermissionCheck } from "@hooks/AllCustomHooks";
import ClientsSelector from "@components/drop-downs/ClientsSelector";
import { useLocation } from "react-router-dom";
import { UserRoles } from "@ts/constants";
import { updateCampaignCreationDisplay } from "@/features/campaignCreationSlice";

interface ILoading {
    isLoading?: IValueBoolean;
}

const Header: React.FC = React.memo(() => {
    const { Option } = Select;
    const {
        resetReviewFilters,
        setRulesSummaryDisp,
        dates,
        DateRange,
        setRequestModalDisp,
    } = useContext<IMainContext>(mainContext) || {};
    const [display, setDisplay] = useState<string>("d-none");
    const title = useSelector((state: ITitle) => state.title.value);
    const isLoading = useSelector((state: ILoading) => state.isLoading.value);
    const activeClientId: number = getClientId();
    const channel = useSelector((state: IChannel) => state.channel.value);
    const channelList = useSelector(
        (state: IChannel) => state.channelList.value
    );
    const campaignCreationDisplay = useSelector(
        (state: ICampaignCreationDisplay) => state.campaignCreationDisplay.value
    );

    const { pathname } = useLocation();

    const dispatch = useDispatch<AppDispatch>();

    // TODO: Can we make this method global as we have similar method in ProductAnalyticsDeepDiveModal.tsx with name changeChannelhandler
    const onChannelSelection = async (value: number) => {
        setActivationConfig(value);
    };

    useEffect(() => {
        const dateLoadingStyle = isLoading
            ? { opacity: "0.7", pointerEvents: "none" }
            : { opacity: "unset", pointerEvents: "unset" };
        $("#reportrange").css(dateLoadingStyle);
    }, [activeClientId, dates, DateRange, isLoading]);

    const userData = useSelector((state: TUserdata) => state.userData.value);

    const userAccess = userData?.is_super_admin
        ? {
              role_slug: UserRoles.ADMIN,
              platforms: [
                  Platforms.ACTIVATION,
                  Platforms.CHATBOT,
                  Platforms.MEASUREMENT,
                  Platforms.PLANNING,
              ],
              clients: [{ client_id: activeClientId }],
          }
        : userData?.access?.find((access: IAccess) =>
              access.clients.find(
                  (client: IClientAccess) => client.client_id === activeClientId
              )
          );
    const platformAccess = userAccess
        ? userAccess?.platforms.map((item: string) => item.toLowerCase())
        : [];
    const userRole: string = userAccess ? userAccess.role_slug : "user";

    const { canViewRequestLog } = usePermissionCheck();

    const { activeModule } = getActiveModule();

    const shouldRenderLoader = useMemo(() => {
        return (
            title !== ParentModule.SETTINGS &&
            activeModule !== ParentModule.REVSTACK &&
            isLoading
        );
    }, [title, activeModule, isLoading, ParentModule]);

    const reportsData = useSelector(
        (state: TReportsData) => state.reportsData.value
    );
    const moduleState = [ParentModule.REVSTACK, SubModule.OVERVIEW].includes(
        activeModule
    )
        ? "revstack"
        : "common";

    const shouldRenderTitle = useMemo(() => {
        if (
            activeModule === ParentModule.REVSTACK ||
            activeModule === ParentModule.COMMON_REPORTS
        ) {
            return reportsData[moduleState].length;
        } else {
            return true;
        }
    }, [activeModule, moduleState, reportsData]);

    return (
        <>
            {userRole === Roles.READONLY && (
                <span className="warning-header">
                    <b>Attention:</b> This account has Read-Only access. You can
                    view information but cannot make changes. For editing
                    capabilities, please contact your system administrator
                </span>
            )}
            <div className="main-header p-2 px-3 d-sm-flex flex-row justify-content-between align-items-center">
                <h4
                    className="flex-grow-1 mb-md-0"
                    data-testid="title"
                    style={{
                        marginLeft: campaignCreationDisplay ? "32px" : "",
                    }}
                >
                    {shouldRenderTitle ? title : ""}
                </h4>

                {shouldRenderLoader && !campaignCreationDisplay && (
                    <div
                        id="header-loader"
                        className="header-loader pe-2"
                        onMouseOut={() => setDisplay("d-none")}
                        onMouseOver={() => setDisplay("")}
                        onClick={() => dispatch(setIsLoader(false))}
                    >
                        <FaTimes
                            className={`close-btn-sml text-danger header-loader-close-btn ${display}`}
                        />
                        <span className="spinner-border text-primary header-loader-spinner" />
                    </div>
                )}
                {title === titleCase(ParentModule.SETTINGS) ||
                title === titleCase(ParentModule.ADMIN_SETTINGS) ||
                (platformAccess.length === 1 &&
                    platformAccess.includes(Platforms.PLANNING)) ||
                campaignCreationDisplay ? null : (
                    <Button
                        className="card ms-1 mx-2"
                        style={
                            title !== titleCase(ParentModule.SETTINGS) &&
                            title !== titleCase(ParentModule.REVSTACK) &&
                            getActiveModule().activeParentModule !==
                                ParentModule.REVIEW_ANALYTICS &&
                            !(
                                platformAccess.length === 1 &&
                                platformAccess.includes(Platforms.PLANNING)
                            ) &&
                            canViewRequestLog
                                ? {
                                      display: "unset",
                                      padding: "3px 8.5px",
                                      borderRadius: "6px",
                                  }
                                : { display: "none" }
                        }
                        onClick={() => setRequestModalDisp(true)}
                    >
                        <FaPaperPlane style={{ fontSize: "16px" }} />
                    </Button>
                )}

                <Button
                    className="btn btn-primary me-2"
                    style={
                        !isLoading && title === titleCase(ParentModule.RULES)
                            ? {
                                  display: "unset",
                                  padding: "5px 8.5px",
                                  fontSize: "0.75rem",
                              }
                            : { display: "none" }
                    }
                    onClick={() => setRulesSummaryDisp(true)}
                >
                    Rules Summary
                </Button>
                {campaignCreationDisplay ? null : (
                    <div
                        id="profile-tab"
                        style={{
                            display: "flex",
                        }}
                    >
                        <div
                            id="channel-selection"
                            style={{
                                display:
                                    ((title ===
                                        titleCase(
                                            ParentModule.ADMIN_SETTINGS
                                        ) ||
                                        activeModule ===
                                            ParentModule.REVSTACK) &&
                                        platformAccess.length === 1 &&
                                        platformAccess.includes(
                                            Platforms.PLANNING
                                        )) ||
                                    (title ===
                                        titleCase(ParentModule.SETTINGS) &&
                                        !pathname.includes(
                                            AdminSettingsTabs.GENERAL
                                        ))
                                        ? "none"
                                        : "unset",
                            }}
                        >
                            <Select
                                showSearch
                                className="channel-selector"
                                value={channel}
                                onChange={onChannelSelection}
                                size="middle"
                                filterOption={(
                                    input,
                                    option: TChannelSelectOptions
                                ) => {
                                    const childrenProps =
                                        option.children?.props?.children[1]
                                            ?.props.children;
                                    return childrenProps?.length
                                        ? filterSelectOptionsWithIdAndName(
                                              input,
                                              childrenProps
                                          )
                                        : false;
                                }}
                            >
                                {channelList.map(
                                    (data: IChannels, index: number) => (
                                        <Option
                                            key={index + 1}
                                            value={data.channel_id}
                                        >
                                            <span>
                                                <img
                                                    className="channel-select-img"
                                                    src={data.logo_url}
                                                    alt={data.name}
                                                    style={{
                                                        objectFit: "contain",
                                                    }}
                                                />
                                                <span className="channel-name">
                                                    {titleCase(data.name)}
                                                    <div
                                                        className="d-none"
                                                        data-account_type={
                                                            data.default_account_type
                                                        }
                                                    >{`${data.channel_id}##`}</div>
                                                </span>
                                            </span>
                                        </Option>
                                    )
                                )}
                            </Select>
                        </div>
                        <ClientsSelector
                            showDefaultClientButton={true}
                            className="me-sm-2"
                            styles={{
                                marginLeft: "8px",
                                width: "250px",
                            }}
                        />
                    </div>
                )}

                {title === titleCase(ParentModule.RULES) ||
                title === titleCase(ParentModule.CHANGE_LOG) ||
                title === titleCase(ParentModule.SETTINGS) ||
                title === titleCase(ParentModule.ADMIN_SETTINGS) ||
                title === titleCase(SubModule.MEDIA_PLANNING) ||
                activeModule === ParentModule.REVSTACK ||
                campaignCreationDisplay ? null : (
                    <DatePicker />
                )}

                {campaignCreationDisplay && (
                    <Button
                        variant="text"
                        color="danger"
                        style={{
                            height: "36px",
                            width: "100px",
                        }}
                        onClick={() => {
                            dispatch(updateCampaignCreationDisplay(false));
                        }}
                    >
                        Exit
                    </Button>
                )}
            </div>
        </>
    );
});

export default Header;
